import { MessageParam } from '@anthropic-ai/sdk/resources'
import OpenAI from 'openai'
import type { ChatCompletionMessageParam } from 'openai/resources/chat/completions'
import { currentEditor, currentUser, load } from 'scrivito'

import { getIamToken } from '../genericApi'
import { getInstanceIdFromUrl } from '../getInstanceIdFromUrl'
import { JrModel } from '@/Atomic/Widgets/AiChatBot/core/engine/config'

const GEN_AI_BASE_URL = 'https://api.justrelate.com/gen-ai/instances'

const AI_DEFAULT_MODEL = JrModel.Standard

async function getClient() {
  const user = (await load(() => currentUser()?.id)) as unknown as string
  const tenant = getInstanceIdFromUrl()
  if (!tenant) {
    throw new Error('Cannot create GenAI client: no instance id in URL')
  }

  let token = await load(() => {
    const editor = currentEditor()
    if (!editor) {
      return ''
    }

    return (editor as unknown as { authToken: () => string }).authToken()
  })

  if (!token) {
    token = await getIamToken(tenant)
  }

  if (!token) {
    console.error('No IAM token available')
    throw new Error('No IAM token available')
  }

  const client = new OpenAI({
    apiKey: token,
    baseURL: `${GEN_AI_BASE_URL}/${tenant}/v1`,
    dangerouslyAllowBrowser: true,
  })

  return { client, user }
}

export async function askAi(
  messages: Array<MessageParam>,
  model: string = AI_DEFAULT_MODEL,
): Promise<string> {
  const { client, user } = await getClient()

  let stream
  try {
    stream = await client.chat.completions.create({
      model: model,
      messages: messages as Array<ChatCompletionMessageParam>,
      stream: true,
      user,
    })
  } catch (error) {
    console.error('Something went wrong', error)
    return ''
  }

  let content = ''
  for await (const chunk of stream) {
    const choice = chunk.choices[0]
    if (choice) {
      const { delta } = choice
      if (delta.content) {
        content += delta.content
      }
    }
  }

  return content
}
